import {useCallback, useEffect, useRef} from 'react';

import {
  type DcdOwnershipSearchSurface,
  logDcdOwnershipLeftNavResultSelectedWithoutSearch,
  logDcdOwnershipSearchOutcome,
} from './dcdOwnershipAnalytics';

type SearchSession = {
  query: string;
  searchSurface: DcdOwnershipSearchSurface;
  useServerSearch: boolean;
  resultCount: number;
  isComplete: boolean;
  hasOutcome: boolean;
};

export const useDcdOwnershipSearchAnalytics = ({
  query,
  searchSurface,
  useServerSearch,
  resultCount,
  isLoading,
  isError,
}: {
  query: string;
  searchSurface: DcdOwnershipSearchSurface;
  useServerSearch: boolean;
  resultCount: number;
  isLoading: boolean;
  isError: boolean;
}) => {
  const sessionRef = useRef<SearchSession>();

  const logAbandonedSearch = useCallback(() => {
    const session = sessionRef.current;
    if (!session?.isComplete || session.hasOutcome || session.resultCount === 0) return;

    logDcdOwnershipSearchOutcome({
      outcome: 'abandoned',
      searchSurface: session.searchSurface,
      useServerSearch: session.useServerSearch,
      resultCount: session.resultCount,
    });
    session.hasOutcome = true;
  }, []);

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
      logAbandonedSearch();
      sessionRef.current = undefined;
      return;
    }

    const previousSession = sessionRef.current;
    const isSameQuery =
      previousSession?.query === normalizedQuery &&
      previousSession.searchSurface === searchSurface &&
      previousSession.useServerSearch === useServerSearch;
    const session: SearchSession = {
      query: normalizedQuery,
      searchSurface,
      useServerSearch,
      resultCount,
      isComplete: !isLoading && !isError,
      hasOutcome: isSameQuery ? previousSession.hasOutcome : false,
    };
    sessionRef.current = session;

    if (!isLoading && !isError && resultCount === 0 && !session.hasOutcome) {
      logDcdOwnershipSearchOutcome({
        outcome: 'no_results',
        searchSurface,
        useServerSearch,
        resultCount: 0,
      });
      session.hasOutcome = true;
    }
  }, [isError, isLoading, logAbandonedSearch, query, resultCount, searchSurface, useServerSearch]);

  useEffect(() => () => logAbandonedSearch(), [logAbandonedSearch]);

  const recordSelection = useCallback(
    ({resultType, rank}: {resultType: string; rank: number}) => {
      if (!query.trim()) {
        logDcdOwnershipLeftNavResultSelectedWithoutSearch({
          searchSurface,
          resultCount,
          resultType,
          rank,
        });
        return;
      }

      const session = sessionRef.current;
      if (!session?.isComplete || session.hasOutcome) return;

      logDcdOwnershipSearchOutcome({
        outcome: 'result_selected',
        searchSurface: session.searchSurface,
        useServerSearch: session.useServerSearch,
        resultCount: session.resultCount,
        resultType,
        rank,
      });
      session.hasOutcome = true;
    },
    [query, resultCount, searchSurface]
  );

  return {recordSelection};
};
